import React, { useState } from "react";
import { experience, projects, skills, education, certifications, personalInfo, publications, linuxFoundation, communityProfiles } from "../../mockData";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import {
  Briefcase, ChevronDown, ChevronUp, MapPin, Calendar,
  Monitor, GraduationCap, Award, Mail, Phone, Linkedin,
  MapPin as MapPinIcon, Code, Cpu, Cloud, Shield, Server, Stethoscope,
  Activity, ArrowRight, Download, ExternalLink, BookOpen, Github,
  FileText, PenTool
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";

const SectionHeader = ({ tag, title, highlight }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""} mb-12`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px w-12 bg-cyan-500/50"></div>
        <span className="text-cyan-400 text-sm font-mono font-medium tracking-wider uppercase">{tag}</span>
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-100">
        {title} <span className="gradient-text">{highlight}</span>
      </h2>
    </div>
  );
};

const ExperienceItem = ({ role, company, period, location, highlights, current, index }) => {
  const [expanded, setExpanded] = useState(current || false);
  const [ref, isVisible] = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`fade-in-up ${isVisible ? "visible" : ""} relative pl-12 pb-10`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="absolute left-[19px] top-2 w-3 h-3 rounded-full border-2 border-cyan-400 bg-[#0a0f1c] z-10"></div>
      {current && (
        <div className="absolute left-[22px] top-[11px] w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot"></div>
      )}
      <div
        className="glass-card rounded-xl p-5 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-base font-semibold text-slate-200">{role}</h3>
              {current && (
                <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/30 text-[10px] px-2 py-0 hover:bg-cyan-500/20">
                  Current
                </Badge>
              )}
            </div>
            <p className="text-cyan-400/80 text-sm font-medium">{company}</p>
            <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar size={12} /> {period}
              </span>
              {location && (
                <span className="flex items-center gap-1">
                  <MapPin size={12} /> {location}
                </span>
              )}
            </div>
          </div>
          <button className="text-slate-500 hover:text-cyan-400 transition-colors p-1 flex-shrink-0">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        {expanded && (
          <ul className="mt-4 space-y-2 border-t border-slate-800/50 pt-4">
            {highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                <ArrowRight size={12} className="text-cyan-500/50 mt-1 flex-shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const ExperienceSection = () => (
  <section id="experience" className="py-24 relative">
    <div className="max-w-4xl mx-auto px-6">
      <SectionHeader tag="Career" title="Professional" highlight="Journey" />
      <div className="relative">
        <div className="timeline-line"></div>
        {experience.map((exp, i) => (
          <ExperienceItem key={i} {...exp} index={i} />
        ))}
      </div>
    </div>
    <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
  </section>
);

const ProjectsSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [expandedProject, setExpandedProject] = useState(null);

  return (
    <section id="projects" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader tag="Portfolio" title="Key Products &" highlight="Projects" />
        <div ref={ref} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, i) => (
            <div
              key={i}
              className={`glass-card rounded-xl overflow-hidden fade-in-up ${isVisible ? "visible" : ""} group`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div
                className="aspect-[16/10] relative overflow-hidden cursor-pointer"
                onClick={() => setExpandedProject(expandedProject === i ? null : i)}
              >
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    const img = e.target;
                    if (!(img instanceof HTMLImageElement)) return;
                    img.style.display = "none";
                    const fallback = img.nextElementSibling;
                    if (fallback instanceof HTMLElement) fallback.style.display = "flex";
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-cyan-900/20 to-slate-900/40 items-center justify-center hidden">
                  <Activity size={32} className="text-cyan-400/40" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-transparent to-transparent opacity-60"></div>
                <Badge className="absolute top-3 left-3 bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-[10px] hover:bg-cyan-500/30">
                  {project.category}
                </Badge>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-base font-semibold text-slate-200">{project.name}</h3>
                  {project.url && (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400/50 hover:text-cyan-400 transition-colors p-1"
                      onClick={(e) => e.stopPropagation()}
                      title="View product page"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
                <p className="text-slate-500 text-xs leading-relaxed mb-2">{project.description}</p>
                {expandedProject === i && (
                  <div className="border-t border-slate-800/50 pt-3 mt-2 animate-in">
                    <p className="text-slate-400 text-xs leading-relaxed mb-3">{project.overview}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {project.tech.map((t, j) => (
                        <span key={j} className="skill-tag text-[10px] py-0.5 px-2">{t}</span>
                      ))}
                    </div>
                    {project.url && (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 flex items-center gap-1 text-cyan-400/60 hover:text-cyan-400 text-xs font-mono transition-colors"
                      >
                        <ExternalLink size={10} /> View product page
                      </a>
                    )}
                  </div>
                )}
                <div
                  className="flex items-center gap-1 mt-2 text-[10px] text-cyan-400/60 font-mono cursor-pointer"
                  onClick={() => setExpandedProject(expandedProject === i ? null : i)}
                >
                  <ArrowRight size={10} />
                  <span>{expandedProject === i ? "Click to collapse" : "Click for details"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

const skillIcons = {
  "Embedded & OS": Cpu,
  "Programming": Code,
  "IoT & Cloud": Cloud,
  "Architecture": Server,
  "DevOps & Tools": Monitor,
  "Cybersecurity": Shield,
  "Medical Standards": Stethoscope,
  "Leadership": Briefcase
};

const SkillsSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const categories = Object.keys(skills);

  return (
    <section id="skills" className="py-24 relative grid-bg">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader tag="Expertise" title="Technical" highlight="Skills" />
        <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""}`}>
          <Tabs defaultValue={categories[0]}>
            <TabsList className="bg-slate-900/50 border border-slate-800/50 p-1.5 mb-8 flex flex-wrap h-auto gap-1 w-full justify-start">
              {categories.map((cat) => {
                const Icon = skillIcons[cat] || Code;
                return (
                  <TabsTrigger
                    key={cat}
                    value={cat}
                    className="text-xs font-medium flex items-center gap-1.5 px-3 py-2 rounded-md data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-400 data-[state=active]:shadow-none text-slate-500 hover:text-slate-300"
                  >
                    <Icon size={13} /> {cat}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {Object.entries(skills).map(([cat, items]) => (
              <TabsContent key={cat} value={cat}>
                <div className="flex flex-wrap gap-2">
                  {items.map((skill, i) => (
                    <span key={i} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

const EducationSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <section id="education" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader tag="Learning" title="Education &" highlight="Certifications" />
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {education.map((edu, i) => (
            <div
              key={i}
              ref={i === 0 ? ref : undefined}
              className={`glass-card rounded-xl p-6 fade-in-up ${isVisible ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={18} className="text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-200">{edu.degree}</h3>
                  <p className="text-cyan-400/80 text-xs font-medium mt-1">{edu.institution}</p>
                  <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
                    <Calendar size={10} /> {edu.period}
                  </p>
                  <p className="text-slate-500 text-xs mt-2">{edu.details}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
            <Award size={18} className="text-amber-400" /> Professional Certifications
          </h3>
          <div className="flex flex-wrap gap-2">
            {certifications.map((cert, i) => (
              <span key={i} className="cert-tag">{cert}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

const PublicationsCommunitySection = () => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <section id="publications" className="py-24 relative grid-bg">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader tag="Thought Leadership" title="Publications &" highlight="Community" />

        <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""}`}>
          {/* Medium Publication */}
          <div className="mb-16">
            <h3 className="text-lg font-semibold text-slate-200 mb-6 flex items-center gap-2">
              <PenTool size={18} className="text-cyan-400" /> Published Articles
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {publications.map((pub, i) => (
                <a
                  key={i}
                  href={pub.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card rounded-xl overflow-hidden group hover:border-cyan-500/30"
                >
                  <div className="aspect-[16/8] relative overflow-hidden">
                    <img
                      src={pub.image}
                      alt={pub.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        const img = e.target;
                        if (!(img instanceof HTMLImageElement)) return;
                        img.style.display = "none";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-transparent to-transparent opacity-70"></div>
                    <Badge className="absolute top-3 left-3 bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] hover:bg-emerald-500/30">
                      {pub.platform}
                    </Badge>
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                      <Calendar size={10} /> {pub.date}
                    </p>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2 group-hover:text-cyan-400 transition-colors">
                      {pub.title}
                    </h4>
                    <p className="text-slate-500 text-xs leading-relaxed mb-3">{pub.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {pub.tags.map((tag, j) => (
                        <span key={j} className="skill-tag text-[10px] py-0.5 px-2">{tag}</span>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-cyan-400/60 text-xs font-mono">
                      <ExternalLink size={10} /> Read on Medium
                    </div>
                  </div>
                </a>
              ))}

              {/* Medium Profile Card */}
              <a
                href={communityProfiles.medium.url}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card rounded-xl p-6 flex flex-col justify-center items-center gap-4 group hover:border-cyan-500/30 min-h-[200px]"
              >
                <BookOpen size={32} className="text-slate-600 group-hover:text-cyan-400 transition-colors" />
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-300 group-hover:text-cyan-400 transition-colors">
                    Follow on Medium
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Writing about cybersecurity, SBOM, and embedded systems in healthcare
                  </p>
                </div>
                <div className="flex items-center gap-1 text-cyan-400/60 text-xs font-mono">
                  <ExternalLink size={10} /> @abrahamlincolng
                </div>
              </a>
            </div>
          </div>

          {/* Linux Foundation */}
          <div className="mb-12">
            <h3 className="text-lg font-semibold text-slate-200 mb-6 flex items-center gap-2">
              <FileText size={18} className="text-cyan-400" /> Linux Foundation & Open Source
            </h3>
            <div className="glass-card rounded-xl p-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* LF Profile Info */}
                <div className="flex-1">
                  <a
                    href={linuxFoundation.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 mb-4 group"
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-700/50">
                      <img
                        src={linuxFoundation.profileImage}
                        alt="Linux Foundation Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const img = e.target;
                          if (!(img instanceof HTMLImageElement)) return;
                          img.style.display = "none";
                          const container = img.parentElement;
                          if (!(container instanceof HTMLElement)) return;
                          const placeholder = document.createElement("div");
                          placeholder.className = "w-full h-full bg-cyan-500/10 flex items-center justify-center";
                          placeholder.innerHTML = '<span class="text-cyan-400 font-mono text-sm font-bold">LF</span>';
                          container.appendChild(placeholder);
                        }}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-200 group-hover:text-cyan-400 transition-colors flex items-center gap-1.5">
                        Linux Foundation Profile <ExternalLink size={12} />
                      </p>
                      <p className="text-xs text-slate-500">Open Source Contributor & Certified Professional</p>
                    </div>
                  </a>

                  {/* Focus Areas */}
                  <div className="bg-[#0d1117] border border-slate-800/50 rounded-lg p-4 font-mono">
                    <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-2">Open Source Focus Areas</p>
                    {linuxFoundation.focusAreas.map((area, i) => (
                      <div key={i} className="flex items-start gap-2 py-1">
                        <span className="text-cyan-500/60 text-xs select-none mt-0.5">$</span>
                        <span className="text-slate-300 text-xs">{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Training & Badges */}
                <div className="md:w-80 flex-shrink-0 space-y-4">
                  <div>
                    <p className="text-xs text-slate-400 font-semibold mb-3 flex items-center gap-1.5">
                      <Award size={13} className="text-amber-400" /> Badges & Certifications
                    </p>
                    <div className="space-y-2">
                      {linuxFoundation.badges.map((badge, i) => (
                        <div key={i} className="flex items-center gap-2 bg-amber-500/5 border border-amber-500/15 rounded-lg px-3 py-2">
                          <Award size={14} className="text-amber-400" />
                          <span className="text-xs text-amber-300/80">{badge.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-slate-400 font-semibold mb-3 flex items-center gap-1.5">
                      <GraduationCap size={13} className="text-cyan-400" /> Training Completed
                    </p>
                    <div className="space-y-2">
                      {linuxFoundation.training.map((t, i) => (
                        <div key={i} className="bg-slate-800/20 border border-slate-800/30 rounded-lg px-3 py-2">
                          <p className="text-xs text-slate-300">{t.name}</p>
                          <p className="text-[10px] text-slate-600 font-mono mt-0.5">{t.code} | {t.date}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* GitHub */}
          <div className="flex gap-4">
            <a
              href={communityProfiles.github.url}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card rounded-xl px-6 py-4 flex items-center gap-3 group hover:border-cyan-500/30"
            >
              <Github size={20} className="text-slate-400 group-hover:text-cyan-400 transition-colors" />
              <div>
                <p className="text-sm font-medium text-slate-300 group-hover:text-cyan-400 transition-colors">GitHub</p>
                <p className="text-xs text-slate-500 font-mono">@abrahamlincolng</p>
              </div>
              <ExternalLink size={12} className="text-slate-600 ml-2" />
            </a>
          </div>
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

const ContactSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <section id="contact" className="py-24 relative grid-bg">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-cyan-500/50"></div>
            <span className="text-cyan-400 text-sm font-mono font-medium tracking-wider uppercase">Connect</span>
            <div className="h-px w-12 bg-cyan-500/50"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4">
            Let's <span className="gradient-text">Connect</span>
          </h2>
          <p className="text-slate-400 max-w-lg mx-auto mb-12">
            I'm passionate about driving innovation in healthcare technology and would welcome
            the opportunity to discuss how my expertise can contribute to transformative
            healthcare solutions.
          </p>
          <div className="flex justify-center mb-6">
            <a
              href={personalInfo.contact.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card rounded-xl p-6 flex flex-col items-center gap-3 hover:border-cyan-500/30 group w-full max-w-xs"
            >
              <Linkedin size={22} className="text-cyan-400 group-hover:scale-110 transition-transform" />
              <span className="text-xs text-slate-500">LinkedIn</span>
              <span className="text-sm text-slate-200 font-medium">View Profile</span>
            </a>
          </div>
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mb-6">
            <MapPinIcon size={14} />
            <span>{personalInfo.contact.location}</span>
          </div>
          <div className="mt-8">
            <a
              href="https://customer-assets.emergentagent.com/job_self-brand-hub-1/artifacts/r7zwvhwl_AbrahamGogulamudiv4.pdf"
              download
              className="inline-flex items-center gap-2 border border-slate-700 hover:border-cyan-500/50 text-slate-400 hover:text-cyan-400 px-5 py-2.5 rounded-lg transition-all duration-300 text-sm"
            >
              <Download size={15} /> Download Resume
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const ExperienceSkillsContact = () => (
  <>
    <ExperienceSection />
    <ProjectsSection />
    <SkillsSection />
    <EducationSection />
    <PublicationsCommunitySection />
    <ContactSection />
  </>
);

export default ExperienceSkillsContact;
